import { createContext, useContext, useState, useEffect, useRef } from "react";
import api from "../Services/api";
import { useAuth } from "./AuthContext";
import { WS_BASE_URL } from "../config";

const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
  const [rooms, setRooms] = useState([]);
  const [activeRoomId, setActiveRoomId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [onlineStatuses, setOnlineStatuses] = useState({});
  const socketRef = useRef(null);
  const [typingUsers, setTypingUsers] = useState({}); // { roomId: { userId: bool } }

  // Fetch all chat rooms the user is a member of
  const fetchRooms = async () => {
    try {
      setLoading(true);
      const res = await api.get("/chat/rooms/");
      const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setRooms(data);
    } catch (err) {
      console.error("Error fetching rooms:", err);
      setRooms([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch messages for a specific room
  const fetchMessages = async (roomId) => {
    if (!roomId) {
      setMessages([]);
      return;
    }
    setMessages([]); // Clear immediately to avoid showing old messages while loading
    try {
      const res = await api.get(`/chat/messages/?room_id=${roomId}`);
      const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setMessages(data);
      // Mark as read
      await api.post(`/chat/rooms/${roomId}/mark_as_read/`);
    } catch (err) {
      console.error("Error fetching messages:", err);
      setMessages([]);
    }
  };

  // Fetch available users for starting new chats
  const fetchUsers = async () => {
    try {
      setUserLoading(true);
      const res = await api.get("/chat/users/");
      const data = Array.isArray(res.data) ? res.data : (res.data?.results || []);
      setAvailableUsers(data);
    } catch (err) {
      console.error("Error fetching users:", err);
      setAvailableUsers([]);
    } finally {
      setUserLoading(false);
    }
  };

  const createGroup = async (groupData) => {
    try {
      const res = await api.post("/chat/rooms/create_group/", {
        name: groupData.name,
        description: groupData.description,
        color: groupData.color,
        member_ids: groupData.members
      });
      const room = res.data;
      setRooms(prev => [room, ...prev]);
      setActiveRoomId(room.id);
      return { success: true, room };
    } catch (err) {
      console.error("Error creating group:", err);
      return { 
        success: false, 
        error: err.response?.data?.error || "Failed to create group" 
      };
    }
  };

  const startPersonalChat = async (userId) => {
    try {
      const existing = rooms.find(r => r.room_type === "personal" && r.other_member?.id === userId);
      if (existing) {
        setActiveRoomId(existing.id);
        return;
      }

      const draftRoomId = `draft_${userId}`;
      const targetUser = availableUsers.find(au => au.id === userId);
      const draftRoom = {
        id: draftRoomId,
        room_type: "personal",
        other_member: targetUser,
        last_message: null,
        unread_count: 0,
        is_draft: true
      };

      setRooms(prev => [draftRoom, ...prev]);
      setActiveRoomId(draftRoomId);
    } catch (err) {
      console.error("Error starting personal chat:", err);
    }
  };

  const sendMessage = async (roomId, text, file = null) => {
    try {
      let targetRoomId = roomId;

      if (String(roomId).startsWith("draft_")) {
        const targetUserId = parseInt(roomId.replace("draft_", ""));
        const resRoom = await api.post("/chat/rooms/start_personal/", { user_id: targetUserId });
        const realRoom = resRoom.data;

        setRooms(prev => prev.map(r => r.id === roomId ? realRoom : r));
        setActiveRoomId(realRoom.id);
        targetRoomId = realRoom.id;

        if (file) {
          const formData = new FormData();
          formData.append("room", targetRoomId);
          formData.append("text", text);
          if (file) formData.append("file", file);

          const res = await api.post("/chat/messages/", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setMessages([res.data]);
          setRooms(prev => prev.map(r => r.id === targetRoomId ? {...r, last_message: res.data} : r));
          return res.data;
        } else {
          const res = await api.post("/chat/messages/", { room: targetRoomId, text });
          setMessages([res.data]);
          setRooms(prev => prev.map(r => r.id === targetRoomId ? {...r, last_message: res.data} : r));
          return res.data;
        }
      }

      if (file) {
        const formData = new FormData();
        formData.append("room", targetRoomId);
        formData.append("text", text);
        if (file) formData.append("file", file);

        const res = await api.post("/chat/messages/", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        // The message will be added to the state via the WebSocket broadcast (triggered by backend signal)
        return res.data;
      } else if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({
          type: 'chat_message',
          message: text
        }));
      } else {
        // Fallback to REST
        const res = await api.post("/chat/messages/", { room: targetRoomId, text });
        // The message will be added via WebSocket broadcast
        return res.data;
      }
    } catch (err) {
      console.error("Error sending message:", err);
    }
  };

  const sendTyping = (isTyping) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: 'typing',
        is_typing: isTyping
      }));
    }
  };

  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      fetchRooms();
      fetchUsers();
    } else {
      setRooms([]);
      setAvailableUsers([]);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (activeRoomId) {
      setRooms(prev => prev.filter(r => !String(r.id).startsWith("draft_") || r.id === activeRoomId));

      if (String(activeRoomId).startsWith("draft_")) {
        setMessages([]);
        return;
      }

      fetchMessages(activeRoomId);

      // Setup WebSocket (authenticates securely via access_token cookie)
      const wsUrl = `${WS_BASE_URL}${activeRoomId}/`;
      
      let socket = null;
      let reconnectTimer = null;
      let reconnectDelay = 2000;
      let intentionallyClosed = false;

      const connect = () => {
        if (intentionallyClosed) return;
        socket = new WebSocket(wsUrl);
        socketRef.current = socket;

        socket.onopen = () => {
          console.log("Chat WS connected");
          reconnectDelay = 2000;
        };

        socket.onmessage = (event) => {
          const data = JSON.parse(event.data);
          if (data.type === 'chat_message') {
            const msg = data.message;
            setMessages(prev => {
              if (prev.some(m => m.id === msg.id)) return prev;
              return [...prev, msg];
            });
            setRooms(prev => prev.map(r => r.id === activeRoomId ? {...r, last_message: msg} : r));
          } else if (data.type === 'typing') {
            setTypingUsers(prev => ({
              ...prev,
              [activeRoomId]: {
                ...(prev[activeRoomId] || {}),
                [data.user_id]: data.is_typing
              }
            }));
          } else if (data.type === 'initial_status') {
            setOnlineStatuses(prev => ({
              ...prev,
              ...data.online_members
            }));
          } else if (data.type === 'status_update') {
            setOnlineStatuses(prev => ({
              ...prev,
              [data.user_id]: data.status
            }));
          }
        };

        socket.onclose = () => {
          if (intentionallyClosed) return;
          console.warn(`Chat WS disconnected. Reconnecting in ${reconnectDelay / 1000}s…`);
          reconnectTimer = setTimeout(() => {
            reconnectDelay = Math.min(reconnectDelay * 2, 30000);
            connect();
          }, reconnectDelay);
        };

        socket.onerror = (err) => {
          console.error("Chat WS error:", err);
        };
      };

      connect();

      return () => {
        intentionallyClosed = true;
        if (reconnectTimer) clearTimeout(reconnectTimer);
        if (socket) socket.close();
      };
    }
  }, [activeRoomId]);

  return (
    <ChatContext.Provider
      value={{
        rooms,
        activeRoomId,
        setActiveRoomId,
        messages,
        setMessages,
        sendMessage,
        fetchRooms,
        fetchUsers,
        availableUsers,
        startPersonalChat,
        createGroup,
        loading,
        userLoading,
        sendTyping,
        typingUsers,
        onlineStatuses
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => useContext(ChatContext);